"use client";

import {
  createContext,
  type ReactNode,
  useContext,
  useState,
  useEffect,
} from "react";
import { getRuntimeEnv } from "@utils/extractEnvFromScript";

interface EnvVariablesClientConfig {
  ACKO_ENV: string | undefined;
  APP_VERSION: string | undefined;
  NEXT_PUBLIC_BASE_URL: string | undefined;
  NEXT_PUBLIC_ACKO_DRIVE_CATALOG_SERVICE: string | undefined;
  NEXT_PUBLIC_ACKO_DRIVE_WORKSPACE: string | undefined;
  NEXT_PUBLIC_BFF_URL: string | undefined;
  NEXT_PUBLIC_DATADOG_APPLICATION_ID: string | undefined;
  NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: string | undefined;
  NEXT_PUBLIC_DATADOG_SITE: string | undefined;
  NEXT_PUBLIC_DATADOG_SERVICE: string | undefined;
  NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE: string | undefined;
  NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE: string | undefined;
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: string | undefined;
}

const defaultEnvVariables: EnvVariablesClientConfig = {
  ACKO_ENV: "",
  APP_VERSION: "",
  NEXT_PUBLIC_BASE_URL: "",
  NEXT_PUBLIC_ACKO_DRIVE_CATALOG_SERVICE: "",
  NEXT_PUBLIC_ACKO_DRIVE_WORKSPACE: "",
  NEXT_PUBLIC_BFF_URL: "",
  NEXT_PUBLIC_DATADOG_APPLICATION_ID: "",
  NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: "",
  NEXT_PUBLIC_DATADOG_SITE: "",
  NEXT_PUBLIC_DATADOG_SERVICE: "",
  NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE: "",
  NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE: "",
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: "",
};

const EnvVariablesClientContext =
  createContext<EnvVariablesClientConfig>(defaultEnvVariables);

interface EnvClientProviderProps {
  children: ReactNode;
}

export const EnvVariablesClientProvider: React.FC<EnvClientProviderProps> = ({
  children,
}) => {
  const [envs, setEnvs] =
    useState<EnvVariablesClientConfig>(defaultEnvVariables);

  useEffect(() => {
    const runtimeEnvs = getRuntimeEnv();
    setEnvs(runtimeEnvs);
  }, []);

  return (
    <EnvVariablesClientContext.Provider value={envs}>
      {children}
    </EnvVariablesClientContext.Provider>
  );
};

export const useEnvVariablesClientConfig = (): EnvVariablesClientConfig => {
  if (EnvVariablesClientContext === undefined) {
    throw new Error(
      "useEnvVariablesClientConfig must be used within an EnvVariablesClientProvider"
    );
  }

  return useContext(EnvVariablesClientContext);
};
